import { App, Form, Modal } from 'antd';
import { useEffect } from 'react';
import { useTranslation } from 'translations/hooks';

import { useGraphqlApiHeaders } from 'business/contextProviders/useApiHeaders';
import { parseRingTimeInputToDateWithoutSeconds } from 'business/production-and-performance/pages/ring-table/services/ring-time';
import {
  GetRingsQuery,
  Module_Enum,
  useUpdateParameterRingMutation,
} from 'generated/graphql';
import apolloClient from 'technical/graphql/client';
import { isUndefinedOrNull } from 'technical/utils/is-null-or-undefined';
import {
  parseDurationInputValueToDurationIsoString,
  parseIsoDurationToDurationInputValue,
} from 'ui/form/duration-input/parse-iso-duration';

import { UpdateRingFormValues } from './types';
import { UpdateRingForm } from './update-ring-form';

interface UpdateRingModalProps {
  isModalVisible: boolean;
  closeModal: () => void;
  ring: GetRingsQuery['parameter_ring'][number] | null;
}

export const UpdateRingModal: React.FC<UpdateRingModalProps> = ({
  isModalVisible,
  closeModal,
  ring,
}) => {
  const { message } = App.useApp();

  const { t } = useTranslation();
  const [form] = Form.useForm<UpdateRingFormValues>();
  const headers = useGraphqlApiHeaders(Module_Enum.ProdPerf);

  const [updateParameterRingMutation] = useUpdateParameterRingMutation({
    context: { headers },
  });

  const save = async (row: UpdateRingFormValues) => {
    await updateParameterRingMutation({
      variables: {
        id: ring?.id,
        updatedFields: {
          ...row,
          keyPosition: row.keyPosition ?? null,
          buildEndMetricPoint: row.buildEndMetricPoint ?? null,
          length: row.length ?? null,
          excavationStart: row.excavationStart?.toISOString() ?? null,
          excavationEnd: row.excavationEnd?.toISOString() ?? null,
          buildStart: row.buildStart?.toISOString() ?? null,
          buildEnd: row.buildEnd?.toISOString() ?? null,
          realExcavationDuration: row.realExcavationDuration
            ? parseDurationInputValueToDurationIsoString(
                row.realExcavationDuration,
              )
            : null,
          realBuildDuration: row.realBuildDuration
            ? parseDurationInputValueToDurationIsoString(row.realBuildDuration)
            : null,
        },
      },
      onCompleted: async () => {
        await apolloClient.reFetchObservableQueries();
        closeModal();
        message.open({
          type: 'success',
          content: t('productionAndPerformance.ring.edit.modal.update.success'),
        });
      },
      onError: () => {
        message.open({
          type: 'error',
          content: t('productionAndPerformance.ring.edit.modal.update.error'),
        });
      },
    });
  };

  useEffect(() => {
    if (ring) {
      form.setFieldsValue({
        ...ring,
        excavationStart: parseRingTimeInputToDateWithoutSeconds(
          ring.excavationStart,
        ),
        excavationEnd: parseRingTimeInputToDateWithoutSeconds(
          ring.excavationEnd,
        ),

        realExcavationDuration: parseIsoDurationToDurationInputValue(
          ring.realExcavationDuration,
        ),
        buildStart: parseRingTimeInputToDateWithoutSeconds(ring.buildStart),
        buildEnd: parseRingTimeInputToDateWithoutSeconds(ring.buildEnd),
        realBuildDuration: parseIsoDurationToDurationInputValue(
          ring.realBuildDuration,
        ),
      });
    }
  }, [form, ring]);

  return (
    <Modal
      title={t('productionAndPerformance.ring.edit.modal.title', {
        ringNumber: ring?.ringNumber,
      })}
      open={isModalVisible}
      onOk={form.submit}
      onCancel={closeModal}
      destroyOnHidden
    >
      {!isUndefinedOrNull(ring) ? (
        <UpdateRingForm form={form} save={save} />
      ) : null}
    </Modal>
  );
};
